/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { GetTimelineEventsQuery } from "~/generated/graphql";
import { ACTION_COLORS, DEFAULT_ACTION_COLOR } from "./constants";
import { TimelineEvent, ResourceScheduleEvent } from "./types";

type IncomingEventResult = NonNullable<
  NonNullable<GetTimelineEventsQuery["incomingEvents"]>["result"]
>[number];

/**
 * Maps incoming events to calendar events — one calendar event PER ACTION.
 * Each action gets its own time slot calculated from the event startTime
 * plus cumulative windowDuration of preceding actions (sorted by order).
 * Colors come from the action's `color` field in the API.
 * Actions from the same plan/event share a `groupId` for visual grouping.
 */
// eslint-disable-next-line complexity
export function mapIncomingEventsToCalendarEvents(
  events: IncomingEventResult[]
): TimelineEvent[] {
  const calendarEvents: TimelineEvent[] = [];

  for (const event of events) {
    if (!event.plan) continue;

    const planActions = [...(event.plan.planActions ?? [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const planName = event.plan.name ?? event.name;
    const planId = event.plan.id ?? "";
    const groupId = event.id;
    const planBorderColor = getPlanGroupColor(planId);
    const eventStart = dayjs(event.startTime);

    // Pre-compute all action time slots
    const actionSlots: Array<{
      start: dayjs.Dayjs;
      end: dayjs.Dayjs;
      windowDuration: number;
    }> = [];
    let preComputeCumulative = 0;
    for (const planAction of planActions) {
      const actionProps = planAction.action as {
        name?: string;
        color?: string;
      };
      const actionName = actionProps?.name ?? planAction.name ?? "Unknown";
      const inputParams: Array<{ key: string; value: string }> = (
        planAction.inputParameters ?? []
      ).filter(
        (p): p is { key: string; value: string } =>
          p.key != null && p.value != null
      );
      const delayParam = inputParams.find((p) => p.key === "DELAY");
      const isDelay =
        actionName.toLowerCase() === "delay" || delayParam != null;
      const windowDuration =
        isDelay && delayParam
          ? (Number(delayParam.value) || planAction.windowDuration) ?? 30
          : planAction.windowDuration ?? 30;

      const start = eventStart.add(preComputeCumulative, "minute");
      const end = start.add(windowDuration - 1, "minute");
      actionSlots.push({ start, end, windowDuration });
      preComputeCumulative += windowDuration;
    }

    for (let i = 0; i < planActions.length; i++) {
      const planAction = planActions[i];
      const actionProps = planAction.action as {
        name?: string;
        color?: string;
      };
      const actionName = actionProps?.name ?? planAction.name ?? "Unknown";
      const actionColor = actionProps?.color ?? DEFAULT_ACTION_COLOR;
      const inputParameters: Array<{ key: string; value: string }> = (
        planAction.inputParameters ?? []
      ).filter(
        (p): p is { key: string; value: string } =>
          p.key != null && p.value != null
      );

      // Calculate total expected resources
      const directResourceCount = planAction.resources?.length ?? 0;
      const groupResourceCount =
        planAction.resourceGroups?.reduce(
          (sum, group) => sum + (group.resourcesCounts?.totalCount ?? 0),
          0
        ) ?? 0;
      const resourceCount = directResourceCount + groupResourceCount;

      const isFirstInGroup = i === 0;
      const isLastInGroup = i === planActions.length - 1;

      const actionStart = actionSlots[i].start;
      const actionEnd = actionSlots[i].end;

      const prevActionEnd =
        i > 0 ? actionSlots[i - 1].end.toISOString() : undefined;
      const nextActionStart =
        i < planActions.length - 1
          ? actionSlots[i + 1].start.toISOString()
          : undefined;

      calendarEvents.push({
        id: `${event.id}-${planAction.id}`,
        title: actionName,
        start: actionStart.toISOString(),
        end: actionEnd.toISOString(),
        planId,
        planName,
        actionName,
        actionOrder: planAction.order ?? 0,
        groupId,
        scheduleTimezone: event.scheduleTimezone,
        resourceCount,
        inputParameters,
        isFirstInGroup,
        isLastInGroup,
        backgroundColor: actionColor,
        borderColor: planBorderColor,
        extendedProps: {
          planId,
          planName,
          actionName,
          actionOrder: planAction.order ?? 0,
          groupId,
          scheduleTimezone: event.scheduleTimezone,
          resourceCount,
          inputParameters,
          isFirstInGroup,
          isLastInGroup,
          isPast: false,
          eventStatus: undefined,
          actionStatus: undefined,
          prevActionEnd,
          nextActionStart,
        },
      });
    }
  }

  return calendarEvents;
}

export function getActionColor(actionName: string): string {
  return ACTION_COLORS[actionName] ?? DEFAULT_ACTION_COLOR;
}

/**
 * Maps past events (from eventsView) to calendar events.
 * Similar to incoming events but marks them as past with reduced opacity.
 */
// eslint-disable-next-line complexity
export function mapPastEventsToCalendarEvents(
  events: Array<{
    id: string;
    name: string;
    startTime: string;
    endTime?: string | null;
    estimatedEndTime: string;
    scheduleTimezone: string;
    status: string;
    plan?: { id: string; name: string } | null;
    actions?: Array<{
      id: string;
      name: string;
      actionName: string;
      order: number;
      windowDuration?: number | null;
      inputParameters?: Array<{ key: string; value: string }> | null;
      action?: { name?: string; color?: string } | null;
      globalState?: { status: string } | null;
      resourceGroups?: Array<{
        resourcesCounts?: { totalCount: number } | null;
      } | null> | null;
      resources?: Array<{ id: string }> | null;
    }> | null;
  }>
): TimelineEvent[] {
  const calendarEvents: TimelineEvent[] = [];

  for (const event of events) {
    // Past events may not have a plan (deleted plans) — still show them
    const actions = [...(event.actions ?? [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );

    if (actions.length === 0) continue;

    const planName = event.plan?.name ?? event.name;
    const planId = event.plan?.id ?? event.id;
    const groupId = `past-${event.id}`;
    const planBorderColor = getPlanGroupColor(planId);
    const eventStart = dayjs(event.startTime);

    // Pre-compute all action time slots for past events
    const pastActionSlots: Array<{ start: dayjs.Dayjs; end: dayjs.Dayjs }> = [];
    let pastCumulative = 0;
    for (const action of actions) {
      const aName =
        action.action?.name ?? action.actionName ?? action.name ?? "Unknown";
      const aParams: Array<{ key: string; value: string }> = (
        action.inputParameters ?? []
      ).filter(
        (p): p is { key: string; value: string } =>
          p.key != null && p.value != null
      );
      const dParam = aParams.find((p) => p.key === "DELAY");
      const isD = aName.toLowerCase() === "delay" || dParam != null;
      const wd =
        isD && dParam
          ? (Number(dParam.value) || action.windowDuration) ?? 30
          : action.windowDuration ?? 30;

      const s = eventStart.add(pastCumulative, "minute");
      const e = s.add(wd - 1, "minute");
      pastActionSlots.push({ start: s, end: e });
      pastCumulative += wd;
    }

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const actionName =
        action.action?.name ?? action.actionName ?? action.name ?? "Unknown";
      const actionColor = action.action?.color ?? DEFAULT_ACTION_COLOR;
      const inputParameters: Array<{ key: string; value: string }> = (
        action.inputParameters ?? []
      ).filter(
        (p): p is { key: string; value: string } =>
          p.key != null && p.value != null
      );

      const directResourceCount = action.resources?.length ?? 0;
      const groupResourceCount =
        action.resourceGroups?.reduce(
          (sum, group) => sum + (group?.resourcesCounts?.totalCount ?? 0),
          0
        ) ?? 0;
      const resourceCount = directResourceCount + groupResourceCount;

      const isFirstInGroup = i === 0;
      const isLastInGroup = i === actions.length - 1;

      const actionStart = pastActionSlots[i].start;
      const actionEnd = pastActionSlots[i].end;

      const prevActionEnd =
        i > 0 ? pastActionSlots[i - 1].end.toISOString() : undefined;
      const nextActionStart =
        i < actions.length - 1
          ? pastActionSlots[i + 1].start.toISOString()
          : undefined;

      calendarEvents.push({
        id: `past-${event.id}-${action.id}`,
        title: actionName,
        start: actionStart.toISOString(),
        end: actionEnd.toISOString(),
        planId,
        planName,
        actionName,
        actionOrder: action.order ?? 0,
        groupId,
        scheduleTimezone: event.scheduleTimezone,
        resourceCount,
        inputParameters,
        isFirstInGroup,
        isLastInGroup,
        backgroundColor: actionColor,
        borderColor: planBorderColor,
        extendedProps: {
          planId,
          planName,
          actionName,
          actionOrder: action.order ?? 0,
          groupId,
          scheduleTimezone: event.scheduleTimezone,
          resourceCount,
          inputParameters,
          isFirstInGroup,
          isLastInGroup,
          isPast: true,
          eventStatus: event.status,
          actionStatus: action.globalState?.status,
          prevActionEnd,
          nextActionStart,
        },
      });
    }
  }

  return calendarEvents;
}

/**
 * Extracts human-readable tooltip settings from inputParameters based on action type.
 * Only shows curated, relevant parameters — not the full list.
 */
export function getTooltipSettings(
  actionName: string,
  inputParameters: Array<{ key: string; value: string }>
): Array<{ label: string; value: string }> {
  const settings: Array<{ label: string; value: string }> = [];
  const lowerName = actionName.toLowerCase();

  if (lowerName.includes("scan") || lowerName.includes("patch")) {
    const patchPolicy = inputParameters.find((p) => p.key === "PatchPolicy");
    if (patchPolicy?.value) {
      settings.push({
        label: "Policy",
        value: formatPatchPolicy(patchPolicy.value),
      });
    }

    const rebootAllowed = inputParameters.find(
      (p) => p.key === "RebootAllowed"
    );
    if (rebootAllowed?.value) {
      settings.push({
        label: "Reboot",
        value: rebootAllowed.value === "true" ? "Yes" : "No",
      });
    }
  }

  if (lowerName === "delay") {
    const delay = inputParameters.find((p) => p.key === "DELAY");
    if (delay?.value) {
      const minutes = Number(delay.value);
      settings.push({
        label: "Duration",
        value:
          minutes >= 60
            ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
            : `${minutes}m`,
      });
    }
  }

  return settings;
}

function formatPatchPolicy(value: string): string {
  switch (value) {
    case "SystemAll":
      return "System All";
    case "SecurityOnly":
      return "Security Only";
    case "Custom":
      return "Custom";
    case "CustomerManaged":
      return "Customer Managed";
    default:
      return value;
  }
}
/**
 * Generates a consistent muted border color for a plan group.
 * Uses a simple hash of the planId to pick from a palette.
 */
const PLAN_GROUP_COLORS = [
  "#5C6BC0", // indigo
  "#26A69A", // teal
  "#8D6E63", // brown
  "#78909C", // blue-grey
  "#7E57C2", // deep purple
  "#66BB6A", // green
  "#FFA726", // orange
  "#EC407A", // pink
];

export function getPlanGroupColor(planId: string): string {
  let hash = 0;
  for (let i = 0; i < planId.length; i++) {
    hash = (hash * 31 + planId.charCodeAt(i)) | 0;
  }
  return PLAN_GROUP_COLORS[Math.abs(hash) % PLAN_GROUP_COLORS.length];
}

export function getDateRangeForView(
  currentDate: Date,
  view: string
): { start: string; end: string } {
  const current = dayjs(currentDate);

  switch (view) {
    case "timeGridDay":
      return {
        start: current.startOf("day").toISOString(),
        end: current.endOf("day").toISOString(),
      };
    case "timeGridWeek":
      return {
        start: current.startOf("week").toISOString(),
        end: current.endOf("week").toISOString(),
      };
    case "dayGridMonth":
      // Fetch a bit extra to cover partial weeks at month boundaries
      return {
        start: current.startOf("month").subtract(7, "day").toISOString(),
        end: current.endOf("month").add(7, "day").toISOString(),
      };
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    default:
      return {
        start: current.startOf("week").toISOString(),
        end: current.endOf("week").toISOString(),
      };
  }
}

export function mapResourceActionsToCalendarEvents(
  futureActions: Array<{
    planId: string;
    actionName: string;
    actionType: string;
    resourceGroupName?: string | null;
    eventId: string;
    eventName: string;
    startTime: string;
    scheduleTimezone: string;
  }>,
  pastActions: Array<{
    planId: string;
    actionName: string;
    actionType: string;
    resourceGroupName?: string | null;
    eventId: string;
    eventName: string;
    endTime?: string | null;
    scheduleTimezone: string;
  }>
): ResourceScheduleEvent[] {
  const futureEvents: ResourceScheduleEvent[] = futureActions.map((action) => ({
    id: `future-${action.eventId}-${action.actionName}`,
    title: action.actionName,
    start: action.startTime,
    planId: action.planId,
    planName: action.eventName,
    actionName: action.actionName,
    actionType: action.actionType,
    resourceGroupName: action.resourceGroupName ?? undefined,
    isPast: false,
    extendedProps: {
      planId: action.planId,
      planName: action.eventName,
      actionName: action.actionName,
      actionType: action.actionType,
      resourceGroupName: action.resourceGroupName ?? undefined,
      isPast: false,
    },
  }));

  // NOTE: PastActionForResource does not expose execution_start_time yet (planned feature).
  // We use `endTime` as the calendar event time — it's the closest available approximation.
  // Once execution_start_time is added to the API, this should be updated.
  const pastEvents: ResourceScheduleEvent[] = pastActions
    .filter((action) => action.endTime != null)
    .map((action) => ({
      id: `past-${action.eventId}-${action.actionName}`,
      title: action.actionName,
      start: action.endTime || "",
      planId: action.planId,
      planName: action.eventName,
      actionName: action.actionName,
      actionType: action.actionType,
      resourceGroupName: action.resourceGroupName ?? undefined,
      isPast: true,
      extendedProps: {
        planId: action.planId,
        planName: action.eventName,
        actionName: action.actionName,
        actionType: action.actionType,
        resourceGroupName: action.resourceGroupName ?? undefined,
        isPast: true,
      },
    }));

  return [...pastEvents, ...futureEvents];
}
